import { ReactComponent as DocumentIcon } from "assets/images/icons/document-dark.svg";
import Dropdown from "bootstrap/js/dist/dropdown";
import { FocusError } from "focus-formik-error";
import { useFormik } from "formik";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import Highlighter from "react-highlight-words";
import { useTranslation } from "react-i18next";
import {
	getPatientDocuments,
	getPatientsSharedDocuments,
	getSinglePatientSharedDocuments,
} from "services/api/taptype/patients";
import { apiHandler, isFunction } from "utils/helpers";
import vars from "utils/vars";
import AutoSave from "views/hooks/AutoSave";

const DocumentsSearchInput = ({
	form,
	attachmentsName = "attachments",
	patient,
	allowLabel = true,
	label = "",
	shouldRefetch,
	setShouldRefetch,
	selectedDocuments,
	setSelectedDocuments,
	hideBadges = false,
	usePatientDocumentsApi = false,
}) => {
	const { t } = useTranslation();
	const { setFieldValue, ...restOfForm } = form;

	// ref hooks
	const dropdownRef = useRef(null);
	const searchInputRef = useRef(null);
	const documentsCancelRequestRef = useRef(null);

	// state hooks
	const [loadMoreDocumentsLoadingState, setLoadMoreDocumentsLoadingState] = useState(false);
	const [isLoadingDocumentsState, setIsLoadingDocumentsState] = useState(false);
	const [paginationState, setPaginationState] = useState({});
	const [documentsState, setDocumentsState] = useState([]);

	// event handler
	const prepareValueForSearch = (value = "") => value?.trim()?.toLowerCase()?.split(" ");
	const getDocumentsAPIHandler = useCallback(
		async (values, { setErrors, isPagination = false, isRefetch = false } = {}) => {
			if (!isRefetch) {
				if (isPagination) setLoadMoreDocumentsLoadingState(true);
				if (!isPagination) setIsLoadingDocumentsState(true);
			}

			if (documentsCancelRequestRef.current?.signal) documentsCancelRequestRef.current?.abort();
			documentsCancelRequestRef.current = new AbortController();
			const documentsApi = usePatientDocumentsApi
				? getPatientDocuments
				: patient?.id
				? getSinglePatientSharedDocuments
				: getPatientsSharedDocuments;

			await apiHandler(documentsApi, {
				data: {
					...(!usePatientDocumentsApi && patient?.id ? { params: { id: patient?.id } } : {}),
					query: values,
					options: { signal: documentsCancelRequestRef.current.signal },
				},
				onError: ({ errors }) => {
					if (errors) setErrors(errors);
					if (!isRefetch) {
						if (isPagination) setLoadMoreDocumentsLoadingState(false);
						if (!isPagination) setIsLoadingDocumentsState(false);
					} else {
						setShouldRefetch(false);
					}
				},
				onSuccess: async (response) => {
					setPaginationState(response?.meta?.pagination || {});
					setDocumentsState((prevState) => [...(isPagination ? prevState : []), ...(response?.data || [])]);
					if (!isRefetch) {
						if (isPagination) setLoadMoreDocumentsLoadingState(false);
						if (!isPagination) setIsLoadingDocumentsState(false);
					} else {
						setShouldRefetch(false);
					}
					if (searchInputRef?.current && "search" in values) Dropdown.getInstance(dropdownRef.current).show();
				},
			});

			if (searchInputRef?.current && "search" in values) searchInputRef.current.focus();
		},
		[patient?.id, setShouldRefetch, usePatientDocumentsApi]
	);
	const onSearchFormikSubmitHandler = async (values, actions) => getDocumentsAPIHandler(values, actions);
	const onReadMoreButtonClickHandler = async (values) => getDocumentsAPIHandler(values, { isPagination: true });
	const onResetClickHandler = async () => {
		searchFormik.resetForm();
		await setSearchFormikFieldValue("search", "");
		if (dropdownRef?.current) Dropdown.getInstance(dropdownRef.current)?.hide();
		searchFormikSubmitForm();
	};
	const onSelectSingleDocumentHandler = async (singleDocument) => {
		const newSelectedDocuments = [...(selectedDocuments || []), singleDocument];
		if (isFunction(setSelectedDocuments)) setSelectedDocuments(newSelectedDocuments);
		if (dropdownRef?.current) Dropdown.getInstance(dropdownRef.current)?.hide();
	};
	const onRemoveSingleDocumentHandler = async (singleDocument) => {
		const newSelectedDocuments = selectedDocuments.filter(({ id }) => id !== singleDocument?.id);
		if (isFunction(setSelectedDocuments)) setSelectedDocuments(newSelectedDocuments);
	};

	// formik
	const {
		submitForm: searchFormikSubmitForm,
		setFieldValue: setSearchFormikFieldValue,
		...searchFormik
	} = useFormik({ initialValues: {}, onSubmit: onSearchFormikSubmitHandler });

	// effect hooks
	useEffect(() => {
		searchFormikSubmitForm();
	}, [searchFormikSubmitForm]);

	useEffect(() => {
		if (shouldRefetch) getDocumentsAPIHandler(searchFormik?.values, { isRefetch: true });
	}, [shouldRefetch, searchFormik?.values, getDocumentsAPIHandler]);

	useEffect(() => {
		const dropdownRefCurrent = dropdownRef?.current;
		let dropdown = Dropdown.getInstance(dropdownRefCurrent);
		if (!dropdown) dropdown = new Dropdown(dropdownRefCurrent);
		return () => (dropdown ? Dropdown.getInstance(dropdownRefCurrent).dispose() : false);
	}, []);

	useEffect(() => {
		if (isFunction(setFieldValue))
			setFieldValue(
				attachmentsName,
				selectedDocuments.map(({ id }) => id)
			);
	}, [attachmentsName, setFieldValue, selectedDocuments]);

	useEffect(() => {
		return () => {
			if (documentsCancelRequestRef.current?.signal) documentsCancelRequestRef.current?.abort();
		};
	}, []);

	return (
		<div className="row gy-3">
			<div className="col-12 position-relative">
				<fieldset>
					<legend className="visually-hidden">search documents:</legend>
					<FocusError
						formik={{
							submitForm: searchFormikSubmitForm,
							setFieldValue: setSearchFormikFieldValue,
							...searchFormik,
						}}
					/>
					<AutoSave
						formik={{
							submitForm: searchFormikSubmitForm,
							setFieldValue: setSearchFormikFieldValue,
							...searchFormik,
						}}
						delay={1000}
					/>
					<div
						className={`row gx-0 flex-nowrap align-items-end input-group border-bottom ${
							!!restOfForm.touched?.[attachmentsName] && !!restOfForm.errors?.[attachmentsName]
								? "border-danger"
								: restOfForm.values?.[attachmentsName] &&
								  !!restOfForm.touched?.[attachmentsName] &&
								  !!!restOfForm.errors?.[attachmentsName]
								? "border-success"
								: ""
						}`}>
						<div className="col">
							<label
								htmlFor="searchDocumentField"
								className={`text-capitalize border-0 ${!allowLabel ? "visually-hidden" : ""}`}>
								{label ? label : "Select Documents"}
							</label>
							<input
								ref={searchInputRef}
								onChange={searchFormik.handleChange}
								value={searchFormik.values?.search || ""}
								type="search"
								name="search"
								id="searchDocumentField"
								className={`form-control form-control-sm border-0 rounded-end-0 bg-transparent hide-clear ps-0 ${
									!!restOfForm.touched?.[attachmentsName] && !!restOfForm.errors?.[attachmentsName]
										? "is-invalid"
										: restOfForm.values?.[attachmentsName] &&
										  !!restOfForm.touched?.[attachmentsName] &&
										  !!!restOfForm.errors?.[attachmentsName]
										? "is-valid"
										: ""
								}`}
								placeholder={label ? label : "Select Documents"}
								spellCheck="false"
								autoComplete="off"
							/>
						</div>
						<div className="input-group-prepend col-auto ms-1 position-static dropdown">
							{searchFormik.values?.search && (
								<button
									type="button"
									className="btn btn-link btn-sm p-2 rounded-0 text-decoration-none d-inline-flex align-items-center justify-content-center op-50 op-100-hover op-100-focus border-0"
									onClick={onResetClickHandler}
									aria-label={t("actions.close")}>
									<svg
										xmlns="http://www.w3.org/2000/svg"
										width="0.75rem"
										height="0.75rem"
										fill="currentColor"
										viewBox="0 0 16 16">
										<path d="M1.293 1.293a1 1 0 0 1 1.414 0L8 6.586l5.293-5.293a1 1 0 1 1 1.414 1.414L9.414 8l5.293 5.293a1 1 0 0 1-1.414 1.414L8 9.414l-5.293 5.293a1 1 0 0 1-1.414-1.414L6.586 8 1.293 2.707a1 1 0 0 1 0-1.414z" />
									</svg>
								</button>
							)}
							<button
								type="button"
								className="btn btn-link btn-sm p-2 text-decoration-none d-inline-flex align-items-center justify-content-center border-0 rounded-0"
								onClick={() => searchFormikSubmitForm()}
								disabled={!searchFormik.isValid}>
								{isLoadingDocumentsState ? (
									<span className="spinner-border spinner-border-sm border-1" role="status">
										<span className="visually-hidden">{`${t("content.loading")}...`}</span>
									</span>
								) : (
									<svg
										xmlns="http://www.w3.org/2000/svg"
										width="0.8rem"
										height="0.8rem"
										viewBox="0 0 37.915 37.915"
										fill="currentColor">
										<path d="M16.7,0a16.7,16.7,0,1,0,16.7,16.7A16.716,16.716,0,0,0,16.7,0Zm0,30.312A13.615,13.615,0,1,1,30.312,16.7,13.63,13.63,0,0,1,16.7,30.312Z"></path>
										<g transform="translate(25.996 25.996)">
											<path
												d="M362.513,360.334l-8.837-8.837a1.541,1.541,0,0,0-2.179,2.179l8.837,8.837a1.541,1.541,0,0,0,2.179-2.179Z"
												transform="translate(-351.046 -351.046)"></path>
										</g>
									</svg>
								)}
							</button>
							<button
								ref={dropdownRef}
								className="btn btn-link btn-sm p-2 text-decoration-none d-inline-flex align-items-center justify-content-center border-0 rounded-end-0 dropdown-toggle dropdown-toggle-with-rotate-icon"
								type="button"
								id="searchDocumentsDropdown"
								data-bs-toggle="dropdown"
								data-bs-display="static"
								data-bs-auto-close="outside"
								aria-expanded="false">
								<span className="btn-icon">
									<svg
										xmlns="http://www.w3.org/2000/svg"
										width="0.8rem"
										height="0.8rem"
										fill="currentColor"
										viewBox="0 0 16 16">
										<path
											fillRule="evenodd"
											d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"
										/>
									</svg>
								</span>
							</button>
							<ul
								className="dropdown-menu shadow rounded border p-0 w-100 start-0 top-100 mt-1 overflow-x-hidden overflow-y-auto"
								aria-labelledby="searchDocumentsDropdown"
								style={{ maxHeight: "200px" }}>
								{documentsState?.length >= 1 ? (
									<>
										{documentsState?.map((singleDocument, singleDocumentIndex) => (
											<Fragment key={singleDocument?.id || singleDocumentIndex}>
												<li>
													<button
														className={`dropdown-item white-space-pre-line p-2 ${
															[...(form?.values?.[attachmentsName] || [])].includes(
																singleDocument?.id
															)
																? "active"
																: ""
														}`}
														type="button"
														onClick={() => onSelectSingleDocumentHandler(singleDocument)}
														disabled={[...(form?.values?.[attachmentsName] || [])].includes(
															singleDocument?.id
														)}>
														<span className="d-flex gap-2 align-items-center">
															<span className="flex-shrink-0">
																<DocumentIcon />
															</span>
															<small className="vstack lh-1 flex-grow-1 justify-content-center">
																<Highlighter
																	searchWords={[
																		...(prepareValueForSearch(
																			searchFormik.values?.search || ""
																		) || []),
																	]}
																	autoEscape={true}
																	textToHighlight={singleDocument?.name || ""}
																	highlightTag={({ children }) => (
																		<strong className="fw-bold">{children}</strong>
																	)}
																/>
																{singleDocument?.file_type && (
																	<em className="op-50">
																		<span>Doc. Type: </span>
																		<Highlighter
																			searchWords={[
																				...(prepareValueForSearch(
																					searchFormik.values?.search || ""
																				) || []),
																			]}
																			autoEscape={true}
																			textToHighlight={
																				vars.documents.types.find(
																					({ value }) =>
																						value ===
																						singleDocument?.file_type
																				)?.label || ""
																			}
																			highlightTag={({ children }) => (
																				<strong className="fw-bold">
																					{children}
																				</strong>
																			)}
																		/>
																	</em>
																)}
															</small>
														</span>
													</button>
												</li>
												{singleDocumentIndex + 1 < documentsState?.length && (
													<li>
														<div className="dropdown-divider"></div>
													</li>
												)}
											</Fragment>
										))}
										{isLoadingDocumentsState ? (
											<>
												<li>
													<div className="dropdown-divider"></div>
												</li>
												<li>
													<button
														tabIndex="-1"
														className="btn btn-link btn-sm disabled placeholder-glow d-flex justify-content-center align-items-center col-6 col-md-4 col-lg-6 col-xl-4"
														aria-hidden="true">
														<strong className="placeholder placeholder-sm bg-primary w-50">
															&nbsp;
														</strong>
													</button>
												</li>
											</>
										) : documentsState?.length &&
										  paginationState?.total_pages > 1 &&
										  +paginationState?.current_page < paginationState?.total_pages ? (
											<>
												<li>
													<div className="dropdown-divider"></div>
												</li>
												<li>
													<button
														type="button"
														className="btn btn-link btn-sm w-100 text-uppercase"
														onClick={() =>
															onReadMoreButtonClickHandler({
																...searchFormik.values,
																page: +paginationState?.current_page + 1,
															})
														}
														disabled={loadMoreDocumentsLoadingState}>
														{loadMoreDocumentsLoadingState ? (
															<span
																className="spinner-border spinner-border-sm"
																role="status">
																<span className="visually-hidden">{`${t(
																	"content.loading"
																)}...`}</span>
															</span>
														) : (
															<strong>{t("actions.load_more")}</strong>
														)}
													</button>
												</li>
											</>
										) : (
											<></>
										)}
									</>
								) : (
									<li className="dropdown-item">
										<span className="vstack fw-bold text-secondary text-center">
											<span className="fs-6 text-capitalize">No Data Found</span>
										</span>
									</li>
								)}
							</ul>
						</div>
					</div>
				</fieldset>
				{!hideBadges && selectedDocuments.length >= 1 && (
					<div className="row g-1 mt-2">
						{selectedDocuments?.map((singleSelectedDocument) => (
							<div className="col-auto" key={singleSelectedDocument?.id}>
								<div className="badge bg-primary-100 ps-3 pe-0 py-1 fw-normal text-body position-relative border border-primary rounded-2 d-inline-flex align-items-start white-space-pre-line text-start">
									<span className="h-100 flex-grow-1" style={{ lineHeight: 1.5 }}>
										{[
											singleSelectedDocument?.name || "",
											vars.documents.types.find(
												({ value }) => value === singleSelectedDocument?.file_type
											)?.label || "",
										]
											.filter(Boolean)
											.join(" - ")
											.trim()}
									</span>
									<button
										type="button"
										className="btn btn-link btn-sm py-0 text-dark text-danger-hover text-danger-focus border-0 rounded-pill text-decoration-none rounded-start-0 ms-2 flex-shrink-0"
										aria-label={t("actions.remove")}
										onClick={() => onRemoveSingleDocumentHandler(singleSelectedDocument)}>
										<svg
											xmlns="http://www.w3.org/2000/svg"
											width="1rem"
											height="1rem"
											fill="currentColor"
											viewBox="0 0 16 16">
											<path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
										</svg>
									</button>
								</div>
							</div>
						))}
					</div>
				)}
				<select
					value={form?.values?.[attachmentsName] || []}
					name={attachmentsName}
					className={`form-select ${
						!!form?.touched?.[attachmentsName] && !!form?.errors?.[attachmentsName]
							? "is-invalid"
							: form?.values?.[attachmentsName] &&
							  !!form?.touched?.[attachmentsName] &&
							  !!!form?.errors?.[attachmentsName]
							? "is-valid"
							: ""
					}`}
					multiple
					disabled
					hidden
					readOnly>
					<option value="" disabled>
						- Select Documents -
					</option>
					{documentsState?.map(({ id, title }) => (
						<option value={id} key={id}>
							{title}
						</option>
					))}
				</select>
				{!!restOfForm.touched?.[attachmentsName] && !!restOfForm.errors?.[attachmentsName] && (
					<div className="invalid-tooltip text-uppercase">
						<strong>{restOfForm.errors?.[attachmentsName]}</strong>
					</div>
				)}
			</div>
		</div>
	);
};

export default DocumentsSearchInput;
